// ============================================================
// TaskFlow OS - Approval Node (React Flow Custom Node)
// ============================================================

import React, { memo, useMemo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { ApprovalStatus } from '../../lib/types';
import { APPROVAL_STATUS_LABELS, APPROVAL_STATUS_COLORS } from '../../lib/constants';
import { IconApproval, IconUser, IconClock } from '../../icons/SvgIcons';

interface ApprovalNodeProps {
  data: {
    label: string;
    approvalStatus: ApprovalStatus;
    approverName?: string;
    approverAvatar?: string;
    requestedByName?: string;
    deadline?: string;
    rejectionReason?: string;
    onEdit?: () => void;
    onApprove?: () => void;
    onReject?: () => void;
  };
  selected: boolean;
}

const ApprovalNodeComponent: React.FC<ApprovalNodeProps> = ({ data, selected }) => {
  const statusColor = APPROVAL_STATUS_COLORS[data.approvalStatus];

  const animClass = useMemo(() => {
    switch (data.approvalStatus) {
      case 'pending': return 'animate-micro-glow';
      case 'approved': return 'animate-glow-fade';
      case 'rejected': return 'animate-shake-error';
      default: return '';
    }
  }, [data.approvalStatus]);

  return (
    <div
      className={`
        relative min-w-[240px] max-w-[300px] rounded-2xl overflow-hidden cursor-pointer
        transition-all duration-500 ease-out ${animClass}
        ${selected ? 'ring-2 ring-yellow-400/40 scale-[1.02]' : ''}
      `}
      style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(24px) saturate(1.8)',
        WebkitBackdropFilter: 'blur(24px) saturate(1.8)',
        border: `1px solid ${selected ? 'rgba(245, 158, 11, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
        boxShadow: `
          inset 0 1px 0 0 rgba(255, 255, 255, 0.12),
          inset 0 -2px 8px 0 rgba(0, 0, 0, 0.2),
          0 8px 32px -4px rgba(0, 0, 0, 0.25),
          0 0 12px -2px ${statusColor}20
        `,
      }}
      onDoubleClick={data.onEdit}
    >
      {/* Refraction layer */}
      <div
        className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none rounded-t-2xl"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, transparent 100%)',
        }}
      />

      {/* Status bar */}
      <div
        className="h-1 w-full"
        style={{
          background: `linear-gradient(90deg, ${statusColor}, ${statusColor}66)`,
          boxShadow: `0 0 8px ${statusColor}40`,
        }}
      />

      <div className="p-4 relative z-10">
        {/* Icon + Title */}
        <div className="flex items-center gap-2 mb-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{
              background: `${statusColor}15`,
              border: `1px solid ${statusColor}30`,
            }}
          >
            <IconApproval size={16} color={statusColor} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-white truncate">{data.label}</h3>
            <span className="text-[10px] text-white/40">承認ノード</span>
          </div>
        </div>

        {/* Status */}
        <div className="flex items-center justify-between mb-2">
          <span
            className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
            style={{
              background: `${statusColor}15`,
              color: statusColor,
              border: `1px solid ${statusColor}30`,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
            {APPROVAL_STATUS_LABELS[data.approvalStatus]}
          </span>

          {data.deadline && (
            <span className="flex items-center gap-1 text-[10px] text-white/40">
              <IconClock size={10} />
              {new Date(data.deadline).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>

        {/* Approver */}
        {data.approverName && (
          <div className="flex items-center gap-1.5 mt-2">
            <div className="w-5 h-5 rounded-full bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center">
              <IconUser size={10} color="#f59e0b" />
            </div>
            <span className="text-[11px] text-white/60">
              承認者: {data.approverName}
            </span>
          </div>
        )}

        {/* Rejection reason */}
        {data.rejectionReason && data.approvalStatus === 'rejected' && (
          <div className="mt-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20">
            <p className="text-[10px] text-red-400">{data.rejectionReason}</p>
          </div>
        )}

        {/* Action buttons for pending */}
        {data.approvalStatus === 'pending' && (data.onApprove || data.onReject) && (
          <div className="flex gap-2 mt-3">
            {data.onApprove && (
              <button
                onClick={(e) => { e.stopPropagation(); data.onApprove?.(); }}
                className="flex-1 py-1.5 rounded-lg text-[11px] font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30 transition-colors"
              >
                承認
              </button>
            )}
            {data.onReject && (
              <button
                onClick={(e) => { e.stopPropagation(); data.onReject?.(); }}
                className="flex-1 py-1.5 rounded-lg text-[11px] font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors"
              >
                却下
              </button>
            )}
          </div>
        )}
      </div>

      <Handle type="target" position={Position.Left} className="!w-3 !h-3 !bg-yellow-500/50 !border-2 !border-yellow-500/80" />
      <Handle type="source" position={Position.Right} className="!w-3 !h-3 !bg-yellow-500/50 !border-2 !border-yellow-500/80" />
    </div>
  );
};

export default memo(ApprovalNodeComponent);
